import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BuyerLayout from "@/components/layouts/BuyerLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, Clock, ArrowLeft, Receipt } from "lucide-react";
import { orderApi, associationApi, propertyApi } from "@/services/api";
import { useToast } from "@/components/ui/use-toast";
import { Order, OrderStatus } from "@/models";
import { downloadInvoicePdf } from "@/utils/invoicePdfUtils";
import { StorageImage, StorageLink } from "@/components/shared/StorageFile";
import { downloadStorageFile, isImageFile } from "@/utils/storageRefs";

const getBadgeVariant = (status: string) => {
  switch (status) {
    case "approved":
      return "success";
    case "under_review":
    case "application_submitted":
      return "warning";
    case "pending_payment":
      return "secondary";
    case "denied":
    case "rejected":
      return "destructive";
    default:
      return "outline";
  }
};

const getStatusText = (status: string) => {
  switch (status) {
    case "approved":
      return "Approved";
    case "under_review":
      return "Under Review";
    case "application_submitted":
      return "Submitted";
    case "pending_payment":
      return "Pending Payment";
    case "denied":
    case "rejected":
      return "Denied";
    default:
      return status.replace(/_/g, " ");
  }
};


function isSignatureOrInitials(type?: string): boolean {
  return type === 'signature' || type === 'initials';
}

const OrderDetail = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: orderResponse, isLoading, error } = useQuery({
    queryKey: ['order', orderId],
    queryFn: async () => {
      const response = await orderApi.getCustomerOrder(Number(orderId));
      
      if (response.order.property_id) {
        try {
          const propertyData = await propertyApi.getById(response.order.property_id);
          response.order.property = propertyData;
        } catch (propertyError) {
          console.error("Error fetching property:", propertyError);
        }
      }
      
      return response;
    },
    meta: {
      onError: (err: Error) => {
        toast({
          variant: "destructive",
          title: "Error loading order",
          description: err.message || "Failed to load order details",
        });
      }
    },
    enabled: !!orderId
  });

  const order = orderResponse?.order;

  const handleDownload = (docId: string) => {
    // Find the document in the order's requested documents
    const orderDocument = order?.requested_documents?.find(doc => doc.id.toString() === docId);
    if (!orderDocument) {
      console.error(`Document with ID ${docId} not found`);
      return;
    }

    // If document has an uploaded file, download it directly
    if (orderDocument.uploaded_file_path) {
      // A private file: download it through a signed link.
      void downloadStorageFile(
        orderDocument.uploaded_file_path,
        `${orderDocument.name || `Document_${docId}`}`
      );
    } else {
      console.log(`No uploaded file found for document ${docId}`);
    }
  };

  if (isLoading) {
    return (
      <BuyerLayout>
        <div className="flex justify-center items-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
        </div>
      </BuyerLayout>
    );
  }

  if (error || !order) {
    return (
      <BuyerLayout>
        <div className="space-y-6">
          <div className="flex items-center">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="mr-4">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">Order Not Found</h1>
          </div>
          <p className="text-gray-500">
            The order you're looking for could not be found.
          </p>
          <Button asChild>
            <Link to="/buyer/orders">View All Orders</Link>
          </Button>
        </div>
      </BuyerLayout>
    );
  }

  const statusHistory = order.status_history || [];
  
  const downloadLinks = order.requested_documents
    ?.filter(doc => !!doc.name)
    ?.map(doc => ({
      id: `doc-${doc.id}`,
      name: doc.name || `Document #${doc.document_id}`,
      docId: doc.document_id.toString()
    })) || [];

  const getPropertyAddress = (): string => {
    if (!order.property) return 'No property address available';
    const { property } = order;
    return `${property.address}${property.suite_unit ? ` ${property.suite_unit}` : ''}, ${property.city}, ${property.state} ${property.zip_code}`;
  };

  return (
    <BuyerLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="mr-4">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Order Details</h1>
              <div className="text-gray-500 space-y-1">
                <p>{order.id} • {new Date(order.created_at).toLocaleString()}</p>
                {order.ip_address && (
                  <p className="text-sm">IP: {order.ip_address}</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={getBadgeVariant(order.status)} className="text-sm px-3 py-1">
              {getStatusText(order.status)}
            </Badge>
            {order.status !== "draft" && order.status !== "pending_payment" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadInvoicePdf(order)}
              >
                <Receipt className="h-4 w-4 mr-2" />
                Download Invoice
              </Button>
            )}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Property</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <h3 className="font-medium">Association</h3>
                  <p>{order.association?.name || "Unknown Association"}</p>
                </div>
                <div>
                  <h3 className="font-medium">Property Address</h3>
                  {order.property ? (
                    <>
                      <p>{order.property.address}</p>
                      <p>
                        {order.property.city}, {order.property.state} {order.property.zip_code}
                      </p>
                    </>
                  ) : (
                    <p className="text-muted-foreground">No property address available</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-2">Documents</h3>
                  <ul className="space-y-2">
                    {order.items?.map((item) => (
                      <li key={item.id} className="flex justify-between text-sm">
                        <span>{item.label}</span>
                        <span className="font-medium">${item.amount.toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex justify-between font-bold mt-4">
                    <span>Total</span>
                    <span>${order.total_amount?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {order.requested_documents && order.requested_documents.map((doc) => (
          <div key={doc.id} className="border rounded-md p-4">
            <h3 className="font-medium text-lg mb-3">{doc.name || `Document #${doc.document_id}`}</h3>
            
            {doc.sections && doc.sections.length > 0 && (
              <div className="mt-3">
                {doc.sections.map((section, idx) => (
                  <div key={`section-${section.section_id}-${idx}`} className="mb-4">
                    <h4 className="font-medium text-sm mb-2">{section.title}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {section.fields.map((field, fieldIdx) => (
                        <div key={`field-${field.id}-${fieldIdx}`} className="text-sm space-y-2">
                          <div className="font-medium">
                            {field.label}:{" "}
                          </div>
                          {field.type === "file" && typeof field.value === "string" && field.value ? (
                            <div className="flex items-center gap-2">
                              {isImageFile(field.value) || isSignatureOrInitials(field.type) ? (
                                <StorageLink
                                  href={field.value}
                                  className="block"
                                  title="Open image in new tab"
                                >
                                  <StorageImage
                                    src={field.value}
                                    alt={field.label}
                                    className="max-w-[96px] max-h-[96px] object-contain border rounded shadow"
                                    style={{ background: '#fff' }}
                                  />
                                </StorageLink>
                              ) : (
                                <StorageLink
                                  href={field.value}
                                  className="flex items-center gap-1 text-brand-blue underline"
                                >
                                  <FileText className="w-4 h-4" />
                                  Download File
                                </StorageLink>
                              )}
                            </div>
                          ) : (
                            <span>
                              {typeof field.value === 'object'
                                ? JSON.stringify(field.value)
                                : field.value}
                            </span>
                          )}
                          {field.index !== undefined && (
                            <span className="text-xs text-gray-500 ml-1">(Index: {field.index})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <Card>
          <CardHeader>
            <CardTitle>Status History</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="relative border-l border-gray-200 ml-3">
              {statusHistory.map((event, index) => (
                <li key={index} className="mb-6 ml-6">
                  <span className="absolute flex items-center justify-center w-6 h-6 bg-white rounded-full -left-3 ring-8 ring-white">
                    <Clock className="w-3.5 h-3.5 text-brand-blue" />
                  </span>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="flex items-center text-lg font-medium">
                      {getStatusText(event.new_status)}
                      <Badge variant={getBadgeVariant(event.new_status)} className="ml-3">
                        {getStatusText(event.new_status)}
                      </Badge>
                    </h3>
                    <time className="text-sm text-gray-500">
                      {new Date(event.created_at).toLocaleDateString()} at {new Date(event.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </time>
                  </div>
                  {event.note && (
                    <p className="mb-4 text-base font-normal text-gray-500">
                      {event.note}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>

        {order.status === OrderStatus.approved && downloadLinks.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
              <CardDescription>
                Download your approved documents
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {downloadLinks.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 border rounded-md">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-brand-blue mr-3" />
                      <span>{doc.name}</span>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDownload(doc.docId)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
            <CardFooter>
              <Button asChild className="w-full">
                <Link to="/buyer/orders">
                  Back to All Orders
                </Link>
              </Button>
            </CardFooter>
          </Card>
        )}
      </div>
    </BuyerLayout>
  );
};

export default OrderDetail;
